import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Translation } from 'react-i18next';
import { get, set, compact } from 'lodash';
import { DragDropContext } from 'react-beautiful-dnd';

import ColumnTitles from './ColumnTitles';
import CoreSubjectsColumn from './CoreSubjectsColumn';
import CoreClassPerYearDroppable from './CoreClassPerYearDroppable';
import ClassList from './ClassList';
import ExpandYearColumn from './ExpandYearColumn';
import ColumnFooters from './ColumnFooters';
import CustomClass from './CustomClass';
import AddRow from './AddRow';
import ClearAll from './ClearAll';
import {
  saveEcap,
  setEcap,
  setEcapClassesMatched,
  getHSClassesByAcadplanV2,
} from '../../../../store/actions';
import { getEcapState } from '../../../../store/selectors';
import {
  getRequiredClassesBySubjectYearSlot,
  getRequiredClassesByAcadPlanBySubject,
  idSubjectMap,
  getSubjectObj,
  getDestinationIndex,
} from './dragDropUtil';
import {
  coreSubjectsEn,
  getAcademicYearTitles,
} from '../../../../copy/ecap/classesSetupStrings';

class ClassesDragAndDrop extends Component {
  constructor(props) {
    super(props);

    this.state = {
      expanded: [false, false, false, false],
      customClass: null,
      draggedClass: null,
      hsClasses: null,
      savedAcadPlan: null,
    };
  }

  get coreClassesPerYearDroppables() {
    return [
      ...getAcademicYearTitles().map((title) => []),
      this.props.coreSubjectsListEn,
    ];
  }

  get anyYearOpen() {
    return this.state.expanded.some((item) => item);
  }

  get customClass() {
    // DEV NOTE: If we ever neeed to find customClass data in existing ECAP / classes_matched / whatever data, do so here
    return this.state.customClass;
  }

  get draggedClass() {
    return this.state.draggedClass;
  }

  // API CALLS

  async componentDidMount() {
    await this.props
      .getHSClassesByAcadplanV2(
        this.props.tenant.id,
        this.props.currentDegree.acadPlan,
        true
      )
      .then((res) => this.setState({ hsClasses: res }));
  }

  componentDidUpdate() {
    if (this.props.currentDegree.acadPlan !== this.state.savedAcadPlan) {
      this.props
        .getHSClassesByAcadplanV2(
          this.props.tenant.id,
          this.props.currentDegree.acadPlan,
          true
        )
        .then((res) =>
          this.setState({
            hsClasses: res,
            savedAcadPlan: this.props.currentDegree.acadPlan,
          })
        );
    }
  }

  updateEcap = (arrKey, arrIndex, arrVal) => {
    const plan = JSON.parse(JSON.stringify(this.props.ecap.plan));

    const { subjectIndex, yearIndex } = this.getSubjectYearInfo(arrKey);

    const requiredClasses = getRequiredClassesBySubjectYearSlot(
      subjectIndex,
      yearIndex,
      this.acadPlan,
      this.props.acadPlanClasses
    );

    // If not multiple req and that slot has a null value, get rid of it
    if (requiredClasses.length <= 1) {
      this.handleSingleReqClass(plan, arrKey, arrIndex, arrVal);
    }

    if (requiredClasses.length > 1) {
      this.handleMultiReqClasses(
        plan,
        arrKey,
        arrIndex,
        arrVal,
        requiredClasses
      );
    }

    this.props.setEcap(plan);
    this.props.saveEcap(this.props.auth.jwtToken, plan);
  };

  resetEcap = () => {
    const plan = JSON.parse(JSON.stringify(this.props.ecap.plan));

    const newClassesMatchedObj = getSubjectObj().subjectSlotsObj;

    set(plan, 'classes_matched', newClassesMatchedObj);

    this.props.setEcap(plan);
    this.props.saveEcap(this.props.auth.jwtToken, plan);
  };

  // EVENTS

  handleSingleReqClass = (plan, arrKey, arrIndex, arrVal) => {
    set(plan, `classes_matched.${arrKey}[${arrIndex}]`, arrVal);
    this.props.setEcap(plan.classes_matched);

    if (plan.classes_matched[arrKey][arrIndex] === null) {
      // Remove any null values from a slot that only has 1 required class
      const compactedArray = compact(plan.classes_matched[arrKey]);

      return set(plan, `classes_matched.${arrKey}`, compactedArray);
    }
  };

  handleMultiReqClasses = (plan, arrKey, arrIndex, arrVal, requiredClasses) => {
    // Make sure the array for this subject/year has all the null values needed after this placement
    if (plan.classes_matched[arrKey].length < 1) {
      set(plan, `classes_matched.${arrKey}[${arrIndex}]`, arrVal);

      // Set everything after to null
      if (arrIndex === 0) {
        const goUntilIndex = requiredClasses.length - arrIndex;

        for (let i = arrIndex + 1; i < goUntilIndex; i++) {
          set(plan, `classes_matched.${arrKey}[${i}]`, null);
        }
      }
      // Set everything before to null
      else if (arrIndex > 0) {
        const firstNullIndex = plan.classes_matched[arrKey].findIndex(
          (placedClass) => !placedClass
        );

        for (let i = firstNullIndex; i < arrIndex; i++) {
          set(plan, `classes_matched.${arrKey}[${i}]`, null);
        }
      }
    } else if (plan.classes_matched[arrKey].length >= 1) {
      set(plan, `classes_matched.${arrKey}[${arrIndex}]`, arrVal);
    }
  };

  toggleExpanded = (index) => {
    this.setState((prevState) => ({
      expanded: prevState.expanded.map((val, i) => (i === index ? !val : val)),
    }));
  };

  onCreateCustomClass = (customClass) => this.setState({ customClass });

  handleCustomClassRedragged = (result, sourceId) => {
    const sourceIndex = this.getSourceIndex(result);

    this.updateEcap(sourceId, sourceIndex, null);
  };

  getRowIndex = (destinationId) => {
    const subjectSubString = destinationId.substr(
      0,
      destinationId.indexOf('_')
    );

    return Object.values(coreSubjectsEn).indexOf(subjectSubString);
  };

  getSubjectSubString = (destinationId) => destinationId.split('_');

  get acadPlan() {
    return this.props.currentDegree.acadPlan;
  }

  onDragUpdate = (update, provided) => {
    const subject = update.destination?.droppableId.slice(
      0,
      update.destination?.droppableId.indexOf('_')
    );
    const year =
      Number(
        update.destination?.droppableId.slice(
          update.destination?.droppableId.indexOf('_') + 1,
          update.destination?.droppableId.length
        )
      ) + 1;
    const rec = getRequiredClassesBySubjectYearSlot(
      idSubjectMap(subject),
      year,
      this.props.currentDegree.acadPlan,
      this.props.acadPlanClasses
    );
    const recommendationText =
      rec.length === 1
        ? `The recommendation for this slot is ${rec[0]?.titleEn}`
        : `The recommendations for this slot are ${rec[0]?.titleEn} and ${rec[1]?.titleEn}`;
    return update.destination === null
      ? provided.announce(
          `You have selected an item from ${update.source.droppableId}`
        )
      : provided.announce(
          `Currently hovering over the ${subject} year ${year} slot. ${recommendationText}`
        );
  };

  onDragStart = (result) => {
    let draggedClass;

    if (result.source.droppableId === 'customClass') {
      draggedClass = this.customClass;
    } else if (result.source.droppableId === 'classList') {
      draggedClass = this.props.allClasses.find(
        (course) => String(course.class_id) === String(result.draggableId)
      );
    } else {
      draggedClass =
        this.props.placedClasses[result.source.droppableId][
          result.source.index
        ];
    }

    this.setState({ draggedClass: JSON.parse(JSON.stringify(draggedClass)) });
  };

  onDragEnd = (result) => {
    const sourceId = get(result, 'source.droppableId');
    const destinationId = get(result, 'destination.droppableId');

    if (!destinationId || sourceId === destinationId) return;

    // DEV NOTE: Figure out out if item should be dropped at index 0 or index 1 here

    const destinationIndex = getDestinationIndex(
      destinationId,
      this.props.placedClasses
    );

    if (sourceId !== 'classList' && sourceId !== 'customClass') {
      this.handleCustomClassRedragged(result, sourceId, this.draggedClass);
    }

    if (sourceId === 'customClass') this.setState({ customClass: null });

    this.updateEcap(destinationId, destinationIndex, this.draggedClass);
  };

  getSubjectYearInfo = (destinationId) => {
    const subjectSubString = this.getSubjectSubString(destinationId);
    const subjectIndex =
      Object.values(coreSubjectsEn).indexOf(subjectSubString[0]) + 1;
    const yearIndex = parseInt(subjectSubString[1], 10) + 1;

    return {
      subjectSubString,
      subjectIndex,
      yearIndex,
    };
  };

  onRemove = (arrKey, spotIndex) => {
    // DEV NOTE: We could worry about removing nulls from our array (i.e., make it blank if length of 1),
    // but let's not worry about that situation if we can (just because this code is so much simpler)

    this.updateEcap(arrKey, spotIndex, null);
  };

  onRename = (arrKey, arrIndex, titleEn) => {
    const obj = get(
      this.props.ecap.plan,
      `classes_matched.${arrKey}[${arrIndex}]`,
      {}
    );

    this.updateEcap(arrKey, arrIndex, { ...obj, titleEn });
  };

  // RENDER

  renderYearColumnCourses = (year, index) => {
    return (
      <Translation>
        {(t) => (
          <>
            {this.coreClassesPerYearDroppables.map((coreClasses, ind) => {
              return coreClasses.map((coreClass, coreClassIndex) => {
                const arrKey = `${coreClass}_${index}`;
                const placedClasses = this.props.placedClasses[arrKey];

                /* eslint-disable react/no-array-index-key */
                return (
                  <CoreClassPerYearDroppable
                    key={`${JSON.stringify(coreClass)}-${ind}-${year}`}
                    subject={coreClass}
                    rowIndex={coreClassIndex}
                    index={index}
                    onClickRemove={(spotIndex) =>
                      this.onRemove(arrKey, spotIndex)
                    }
                    onRename={(spotIndex, val) =>
                      this.onRename(arrKey, spotIndex, val)
                    }
                    acadPlanClasses={this.props.acadPlanClasses}
                    acadPlan={this.props.currentDegree.acadPlan}
                    placedClasses={placedClasses}
                    requiredClassesByAcadPlanBySubject={getRequiredClassesByAcadPlanBySubject(
                      idSubjectMap(coreClass),
                      this.props.acadPlanClasses,
                      this.props.currentDegree.acadPlan
                    )}
                    hsClasses={this.state.hsClasses}
                    currentDraggingClass={this.state.draggedClass}
                    style={{
                      color: 'black',
                      overflowY: 'visible',
                      transform: 'none !important',
                      background: coreClassIndex % 2 ? '#F3F1F1' : 'white',
                    }}
                  />
                );
                /* eslint-enable react/no-array-index-key */
              });
            })}
            <ColumnFooters
              tenant={this.props.tenant}
              year={index + 1}
              closeYearClickHandler={() => this.toggleExpanded(index)}
            />
          </>
        )}
      </Translation>
    );
  };

  renderYearColumn(year, index) {
    return (
      <div
        className="column-footer-container classes-drag-drop-column"
        key={`${year}-column-${this.props.index}`}
      >
        <div
          className="year-column-container"
          style={{
            borderLeft: `1px ${index === 3 ? 'transparent' : '#E0E0E0'} solid`,
            borderRight: `1px ${index === 0 ? 'transparent' : '#E0E0E0'} solid`,
          }}
        >
          {this.state.expanded[index] && !this.props.ecapNotAllowed ? (
            this.renderYearColumnCourses(year, index)
          ) : (
            <ExpandYearColumn
              disabled={this.props.ecapNotAllowed}
              color={this.props.tenant.primary}
              overRideStyle="expand-year-button"
              index={index}
              expandYearClickHandler={() => this.toggleExpanded(index)}
            />
          )}
        </div>
      </div>
    );
  }

  render() {
    if (!this.props.acadPlanReqClasses[this.props.currentDegree.acadPlan]) {
      return null;
    }

    return (
      <Translation>
        {(t) => (
          <div id="classes-drag-drop-container">
            <div className="classes-core-year-list-columns-container">
              <ColumnTitles />
              <div className="classes-core-year-columns-container">
                <CoreSubjectsColumn
                  tenant={this.props.tenant}
                  coreSubjectsList={this.props.coreSubjectsList || []}
                  coreSubjectsListEn={this.props.coreSubjectsListEn || []}
                  modalImage={this.props.currentDegree.career_image}
                  acadPlanClasses={
                    this.props.acadPlanReqClasses[
                      this.props.currentDegree.acadPlan
                    ]
                  }
                  placedClasses={this.props.placedClasses}
                  progressByDegree={this.props.progressByDegree}
                />
                <DragDropContext
                  onDragEnd={this.onDragEnd}
                  onDragStart={this.onDragStart}
                  onDragUpdate={this.onDragUpdate}
                >
                  {['year 1', 'year 2', 'year 3', 'year 4'].map((year, index) =>
                    this.renderYearColumn(year, index)
                  )}
                  <div className="class-list-custom-class-container">
                    <ClassList
                      id="classes-drag-drop-column"
                      isMobile={false}
                      tenant={this.props.tenant}
                      yearViewOpen={this.anyYearOpen}
                      allClasses={this.props.allClasses}
                      placedClasses={this.props.placedClasses}
                    />
                    <CustomClass
                      customClass={this.customClass}
                      inputText={t('Class Name')}
                      onDragEnd={this.onDragEnd}
                      onCreate={this.onCreateCustomClass}
                      placedClasses={this.props.placedClasses}
                      tenant={this.props.tenant}
                    />
                  </div>
                </DragDropContext>
              </div>
            </div>
            <div className="add-row-clear-all-container">
              <AddRow
                color={this.props.tenant.primary}
                handleAddRowSelected={this.props.addElectivesRow}
                electivesRowAdded={Object.keys(
                  this.props.placedClasses
                ).includes('Additional Electives_0')}
              />
              <ClearAll
                color={this.props.tenant.primary}
                handleClearAllSelected={this.resetEcap}
              />
            </div>
          </div>
        )}
      </Translation>
    );
  }
}

const mapStateToProps = (state) => {
  const ecap = getEcapState(state);
  const tenant = state.tenant;
  const auth = state.auth;

  return { tenant, ecap, auth };
};

export default connect(mapStateToProps, {
  setEcap,
  saveEcap,
  setEcapClassesMatched,
  getHSClassesByAcadplanV2,
})(ClassesDragAndDrop);
